import { AxiosInstance } from 'axios';
import { PaginationResponse, ProductRevision, ProductUploadRevision } from '../types/product';
import { Filters, SortingRule } from 'react-table';
import { Product, ProductPagination, ProductStatusOptions } from '../types/product';
import { IDocumentService } from '@price-for-profit/micro-services';
import { PRODUCT_UPLOAD_CHUNK_SIZE } from 'shared/constants';
import { chunkArray, formatProduct } from 'shared/utility';

export interface IProductService {
    getProducts: (props: GetDataProps<Product>) => Promise<PaginationResponse>;
    getProduct: (id: string) => Promise<Product>;
    getOptions: (column: string) => Promise<string[]>;
    exportProducts: (props: ExportProps<Product>) => Promise<void>;
    exportPriceSheet: (props: ExportPriceSheetProps) => Promise<void>;
    revisePrice: (props: RevisePriceProps) => Promise<string>;
    changeStatus: (props: ChangeStatusProps) => Promise<string>;
    exportRevisionProducts: (props: ExportProps<Product>) => Promise<void>;
    uploadProducts: (props: UploadProductsProps) => Promise<string>;
}

type GetDataProps<T extends object> = {
    pageIndex: number;
    pageSize: number;
    sortBy: SortingRule<T>[];
    filters: Filters<T>;
    search: string;
    showApproved: boolean;
};

export type ExportProps<T extends object> = {
    sortBy: SortingRule<T>[];
    filters: Filters<T>;
    search: string;
    showApproved: boolean;
};

export type ExportPriceSheetProps = {
    type: 'current' | 'last';
};

export type RevisePriceProps = {
    id: string;
    data: ProductRevision;
};

export type ChangeStatusProps = {
    productIds: string[];
    status: ProductStatusOptions;
};

export type UploadProductsProps = {
    products: ProductUploadRevision[];
    progress: {
        incrementProgress(): void;
        setTotal(total: number): void;
    };
};

type Case = string | boolean | number | (() => string | boolean | number | void);
type SwitchCases = { [key: string]: Case } & Object;

//const executeIfFunction = (f: Case) => (f instanceof Function ? f() : f);
const switchCase = (cases: SwitchCases) => (defaultCase: Case) => (key: string) =>
    cases.hasOwnProperty(key) ? cases[key] : defaultCase;

export class ProductService implements IProductService {
    constructor(private httpClient: AxiosInstance, private documentService: IDocumentService) {}
    async getProducts(props: GetDataProps<Product>) {
        const { pageIndex, pageSize } = props;

        const { data, headers } = await this.httpClient.get<Product[]>(
            this.getUrlByFilters(`/api/products?pageSize=${pageSize}&pageNumber=${pageIndex}`, props)
        );

        const pagination: ProductPagination = JSON.parse(headers['x-pagination'] || '{}');
        return {
            products: data.map(formatProduct),
            ...pagination,
        };
    }

    async getProduct(id: string) {
        const { data } = await this.httpClient.get<Product>(`/api/products/${id}`);
        return formatProduct(data);
    }

    async getOptions(column: string) {
        const { data } = await this.httpClient.get<string[]>(`/api/products/filters?column=${column}`);
        return data;
    }

    async exportProducts(props: ExportProps<Product>) {
        const url = this.getUrlByFilters(`/api/export/products?`, props);
        const { data, headers } = await this.httpClient.get<Blob>(url, {
            headers: {
                'Content-Type': 'text/csv',
            },
            responseType: 'blob',
        });
        const fileName = headers['Content-Disposition']?.split('=')[1] || 'ProductExport.csv';

        this.handleBlob(fileName, data);
    }

    async exportRevisionProducts(props: ExportProps<Product>) {
        const url = this.getUrlByFilters(`/api/export/revise-products?`, props);
        const { data, headers } = await this.httpClient.get<Blob>(url, {
            headers: {
                'Content-Type': 'text/csv',
            },
            responseType: 'blob',
        });
        const fileName = headers['Content-Disposition']?.split('=')[1] || 'RevisionProductExport.csv';

        this.handleBlob(fileName, data);
    }

    async exportPriceSheet({ type }: ExportPriceSheetProps) {
        const url = `/api/export/price-sheet/${type}`;

        const { data, headers } = await this.httpClient.get<Blob>(url, {
            headers: {
                'Content-Type': 'text/csv',
            },
            responseType: 'blob',
        });

        const fileName = headers['Content-Disposition']?.split('=')[1] || 'priceSheetExport.csv';

        this.handleBlob(fileName, data);
    }

    async uploadProducts({ products, progress }: UploadProductsProps) {
        const url = `/api/products/upload`;
        const chunks = chunkArray<ProductUploadRevision>(products, PRODUCT_UPLOAD_CHUNK_SIZE);
        progress.setTotal(chunks.length);
        for (const chunk of chunks) {
            await this.httpClient.post<string>(url, { products: chunk });
            progress.incrementProgress();
        }

        return 'Upload successful!';
    }

    async revisePrice({ id, data }: RevisePriceProps) {
        const url = `/api/products/${id}/revise`;
        const { data: successMessage } = await this.httpClient.post<string>(url, data);

        return successMessage;
    }

    async changeStatus(payload: ChangeStatusProps) {
        const url = `/api/products/status`;

        const { data: successMessage } = await this.httpClient.post<string>(url, payload);

        return successMessage;
    }

    private getUrlByFilters(url: string, { showApproved, sortBy, filters, search }: ExportProps<Product>) {
        if (showApproved) {
            url = url.concat(`&showApproved=${showApproved}`);
        }

        if (sortBy.length) {
            url = this.addSort(url, sortBy);
        }
        if (filters.length) {
            url = this.addFilters(url, filters);
        }
        if (search) {
            url = this.addSearch(url, search);
        }
        return url;
    }

    private addSearch(url: string, search: string) {
        return url.concat(`&search=${encodeURIComponent(search)}`);
    }

    private addSort(url: string, sort: SortingRule<Product>[]) {
        return url.concat(
            `&sort=${encodeURIComponent(sort.map(({ id, desc }) => `${id}${desc ? ' desc' : ''}`).join())}`
        );
    }

    private addFilters(url: string, filters: Filters<Product>) {
        const chooseOperator = switchCase({ lastApproved: 'ge' })('eq');
        return url.concat(
            `&filter=${encodeURIComponent(
                filters.map(({ id, value }) => `${id};${chooseOperator(id)};${value}`).join()
            )}`
        );
    }

    private handleBlob(fileName: string, blob: Blob) {
        const blobUrl = URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        link.parentNode?.removeChild(link);
    }

    // TODO need to revisit this when/if doc service becomes a complete requirement for this app
    // refactoring the doc service changes from the server to the app will take more work than initially anticipated. KH

    // private upsertProductDocument(){
    //   try {
    //     //update
    //   } catch (error) {
    //     if(isAxiosError(error)){
    //       if(error.response?.status === 404){
    //         // insert
    //       }
    //     }
    //   }
    // }
}
